import {
  Box,
  Button,
  ComboboxData,
  ComboboxItem,
  Divider,
  Grid,
  Group,
  InputWrapper,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
  Radio,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconBellCheck,
  IconCalendar,
  IconChevronRight,
  IconDeviceFloppy,
  IconUser,
} from "@tabler/icons-react"
import { useNavigate } from "react-router-dom"

import { AlertsResponses } from "@costory/types/endpoints/alerts"
import { Filters } from "@costory/types/filters"
import { AlertType } from "@costory/types/prisma-client"

import { AlertFormData } from "@costory/front/pages/Alerts/AlertForm"
import { LimitInputCard } from "@costory/front/pages/Alerts/LimitInputCard"
import { AlertLevels } from "@costory/front/pages/Alerts/types"

type ComboboxItemWithProps<T> = ComboboxItem & T
type BudgetItem = AlertsResponses.AlertFormData["budgets"][number]

const tabs: TabDataType[] = [
  {
    key: "what",
    icon: <IconAlertCircle size={16} />,
    label: "What",
    value: 0,
  },
  {
    key: "when",
    icon: <IconCalendar size={16} />,
    label: "When",
    value: 1,
  },
  {
    key: "limits",
    icon: <IconAlertTriangle size={16} />,
    label: "Limits",
    value: 2,
  },
  {
    key: "who",
    icon: <IconUser size={16} />,
    label: "Who",
    value: 3,
  },
] as const

type SlackChannel = { value: string; label: string }

interface Props {
  form: UseFormReturnType<AlertFormData>
  activeTab: number
  setActiveTab: (tab: number) => void
  virtualDimensionsSelectItems: ComboboxItem[]
  virtualDimensionValueSelectItems: ComboboxItemWithProps<{ exists: boolean }>[]
  periodSelectItems: ComboboxData
  currencySelectItems: ComboboxData
  businessMetricsSelectItems: ComboboxData
  ownerSelectItems: ComboboxItem[]
  existingSlackChannels: SlackChannel[]
  slackUsers: SlackChannel[]
  setSearchTerm: (term: string) => void
  filteredSlackItems: () => SlackChannel[]
  isEditMode: boolean
  filters: Filters | undefined
  budgets: AlertsResponses.AlertFormData["budgets"]
}

type TabDataType = {
  key: string
  icon: React.ReactNode
  label: string
  value: number
}

const DynamicTab = ({ value, label, icon }: TabDataType) => (
  <Tabs.Tab value={value.toString()} leftSection={icon}>
    {label}
  </Tabs.Tab>
)

export const AlertConfigurationForm = ({
  form,
  activeTab,
  setActiveTab,
  virtualDimensionsSelectItems,
  virtualDimensionValueSelectItems,
  periodSelectItems,
  currencySelectItems,
  businessMetricsSelectItems,
  ownerSelectItems,
  existingSlackChannels,
  slackUsers,
  setSearchTerm,
  filteredSlackItems,
  isEditMode,
  filters,
  budgets,
}: Props) => {
  const navigate = useNavigate()

  const validateConfig = (): boolean => {
    const { name, redLimit, orangeLimit, yellowLimit } = form.values

    if (!name?.trim()) {
      form.setFieldError("name", "Alert name is required")
      return false
    }

    if (redLimit <= orangeLimit) {
      form.setFieldError(
        "redLimit",
        "Red limit must be greater than orange limit",
      )
      return false
    }

    if (orangeLimit <= yellowLimit) {
      form.setFieldError(
        "orangeLimit",
        "Orange limit must be greater than yellow limit",
      )
      return false
    }

    if (form.validateField("name").hasError) return false
    const tabValidationMap: Record<number, string[]> = {
      0: ["virtualDimensionId", "virtualDimensionValue"],
      1: ["period", "currency", "businessMetricId"],
      2: ["redLimit", "orangeLimit", "yellowLimit"],
      3: ["ownerId", "slackChannelIds"],
    }

    const hasErrors = (tabValidationMap[activeTab] ?? []).some(
      (field) => form.validateField(field).hasError,
    )

    if (hasErrors) return false

    return true
  }

  const handleTabChange = (value: string | null) => {
    const nextTab = value ? parseInt(value, 10) : null
    if (nextTab === null || nextTab === activeTab) return
    if (nextTab > activeTab) {
      const isValid = validateConfig()
      if (isValid) {
        setActiveTab(nextTab)
      }
      return
    }
    setActiveTab(nextTab)
  }

  return (
    <Grid.Col span={{ base: 12, md: 7 }}>
      <Paper shadow="xs" p="lg" radius="md" withBorder>
        <Title order={3} mb="xs">
          Alert Configuration
        </Title>
        <Text c="dimmed" mb="lg">
          Define when and how you want to be notified
        </Text>

        <Tabs value={activeTab?.toString()} onChange={handleTabChange} mb="xl">
          <Tabs.List>
            {tabs.map(({ key, ...tab }) => (
              <DynamicTab key={key} {...tab} />
            ))}
          </Tabs.List>
        </Tabs>

        <Stack gap="lg">
          <InputWrapper label="Alert name">
            <TextInput
              {...form.getInputProps("name")}
              placeholder="Ex: Production environnment monthly cost limit"
              error={!form.values.name?.trim() && "Alert name is required"}
            />
          </InputWrapper>

          {activeTab === 0 && (
            <Stack gap="md">
              <InputWrapper
                label="Alert type"
                description="Choose the type of alert you want to create"
              >
                <Radio.Group {...form.getInputProps("type")}>
                  <Stack gap="xs">
                    <Radio
                      value={AlertType.THRESHOLD}
                      label="Threshold Alert"
                      description="Alert when forecasted spend hits a critical threshold"
                    />
                    <Radio
                      value={AlertType.BUDGET_COMPARISON}
                      label="Budget Comparison Alert"
                      description="Alert when forecasted spend exceeds budget by a percentage"
                    />
                    <Tooltip label="This alert type is not available yet">
                      <Radio
                        value={AlertType.VD_COST_SHIFT}
                        label="Virtual Dimension Cost Shift Alert"
                        description="Alert when virtual dimension costs shift by a percentage"
                        disabled
                      />
                    </Tooltip>
                  </Stack>
                </Radio.Group>
              </InputWrapper>

              <Grid columns={12} gutter="md">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <InputWrapper label="Select a virtual dimension" flex={1}>
                    <Tooltip
                      disabled={!isEditMode}
                      label="This value cannot be edited, please create a new alert"
                    >
                      <Select
                        {...form.getInputProps("virtualDimensionId")}
                        data={virtualDimensionsSelectItems}
                        placeholder="Ex: Environnments"
                        searchable
                        disabled={isEditMode}
                      />
                    </Tooltip>
                  </InputWrapper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <InputWrapper label="Select a value" flex={1}>
                    <Tooltip
                      disabled={!isEditMode}
                      label="This value cannot be edited, please create a new alert"
                    >
                      <Select
                        data={virtualDimensionValueSelectItems}
                        placeholder="Ex: Production"
                        disabled={
                          isEditMode ||
                          virtualDimensionValueSelectItems.length === 0
                        }
                        renderOption={(item) => (
                          <Group justify="space-between" flex={1}>
                            <Box>{item.option.label}</Box>
                            {(
                              item.option as ComboboxItemWithProps<{
                                exists: boolean
                              }>
                            ).exists && (
                              <Tooltip label="There is already an alert for this value">
                                <IconBellCheck color="gray" />
                              </Tooltip>
                            )}
                          </Group>
                        )}
                        {...form.getInputProps("virtualDimensionValue")}
                      />
                    </Tooltip>
                  </InputWrapper>
                </Grid.Col>
              </Grid>
            </Stack>
          )}

          {activeTab === 1 && (
            <Stack gap="md">
              <Grid columns={12} gutter="md">
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <InputWrapper label="Select a period">
                    <Tooltip
                      disabled={!isEditMode}
                      label="This value cannot be edited, please create a new alert"
                    >
                      <Select
                        {...form.getInputProps("period")}
                        data={periodSelectItems}
                        placeholder="Ex: MONTHLY"
                        disabled={isEditMode}
                      />
                    </Tooltip>
                  </InputWrapper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                  <InputWrapper label="Select a currency">
                    <Tooltip
                      disabled={!isEditMode}
                      label="This value cannot be edited, please create a new alert"
                    >
                      <Select
                        {...form.getInputProps("currency")}
                        data={currencySelectItems}
                        placeholder="Ex: USD"
                        disabled
                      />
                    </Tooltip>
                  </InputWrapper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                  <InputWrapper label="Select a metric">
                    <Tooltip
                      disabled={!isEditMode}
                      label="This value cannot be edited, please create a new alert"
                    >
                      <Select
                        {...form.getInputProps("businessMetricId")}
                        data={businessMetricsSelectItems}
                        placeholder="Ex: Cost"
                        disabled={isEditMode}
                      />
                    </Tooltip>
                  </InputWrapper>
                </Grid.Col>
              </Grid>

              {form.values.type === AlertType.BUDGET_COMPARISON && (
                <InputWrapper
                  label="Select a budget"
                  description="Choose the budget to compare forecasted spend against"
                  required
                >
                  <Select
                    {...form.getInputProps("budgetVersionId")}
                    data={budgets
                      .filter(
                        (budget: BudgetItem) =>
                          budget.virtualDimensionId ===
                          form.values.virtualDimensionId,
                      )
                      .map((budget: BudgetItem) => ({
                        value: budget.versionId,
                        label: `${budget.name}${
                          budget.versionName ? ` - ${budget.versionName}` : ""
                        }`,
                      }))}
                    placeholder="Select a budget"
                    searchable
                    disabled={isEditMode || !form.values.virtualDimensionId}
                  />
                </InputWrapper>
              )}
            </Stack>
          )}

          {activeTab === 2 && (
            <Stack mt={10} gap="sm">
              {form.values.type === AlertType.THRESHOLD && (
                <InputWrapper
                  label="Alert based on"
                  description="Choose whether to alert based on actual costs or forecasted end-of-period values"
                >
                  <Switch
                    {...form.getInputProps("useForecastedValues", {
                      type: "checkbox",
                    })}
                    label="Use forecasted values"
                    description="Alert when forecasted end-of-period cost exceeds limits (e.g., if on day 6 of the month, forecast shows exceeding budget by month end)"
                  />
                </InputWrapper>
              )}

              {form.values.type === AlertType.BUDGET_COMPARISON && (
                <Text size="sm" c="dimmed" mb="md">
                  Set percentage thresholds for budget overrun. For example, if
                  forecasted spend exceeds budget by 10%, 20%, or 30%.
                </Text>
              )}

              {form.values.type === AlertType.VD_COST_SHIFT && (
                <Text size="sm" c="dimmed" mb="md">
                  Set percentage thresholds for cost shifts. For example, if
                  costs increase or decrease by 10%, 20%, or 30% compared to the
                  previous period.
                </Text>
              )}

              <LimitInputCard
                level={AlertLevels.Red}
                filters={filters}
                form={form}
                onClickSuggestValue={(value) =>
                  form.setFieldValue("redLimit", value)
                }
                isPercentage={
                  form.values.type === AlertType.BUDGET_COMPARISON ||
                  form.values.type === AlertType.VD_COST_SHIFT
                }
              />
              <LimitInputCard
                level={AlertLevels.Orange}
                filters={filters}
                form={form}
                onClickSuggestValue={(value) =>
                  form.setFieldValue("orangeLimit", value)
                }
                isPercentage={
                  form.values.type === AlertType.BUDGET_COMPARISON ||
                  form.values.type === AlertType.VD_COST_SHIFT
                }
              />
              <LimitInputCard
                level={AlertLevels.Yellow}
                filters={filters}
                form={form}
                onClickSuggestValue={(value) =>
                  form.setFieldValue("yellowLimit", value)
                }
                isPercentage={
                  form.values.type === AlertType.BUDGET_COMPARISON ||
                  form.values.type === AlertType.VD_COST_SHIFT
                }
              />
            </Stack>
          )}

          {activeTab === 3 && (
            <Grid columns={12} gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <InputWrapper label="Select an owner">
                  <Select
                    {...form.getInputProps("ownerId")}
                    data={ownerSelectItems}
                    placeholder="Type to search"
                    searchable
                  />
                </InputWrapper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <InputWrapper label="Select slack channels">
                  <MultiSelect
                    {...form.getInputProps("slackChannelIds")}
                    data={[...existingSlackChannels, ...slackUsers]}
                    onSearchChange={(term) => setSearchTerm(term)}
                    filter={filteredSlackItems}
                    placeholder="Type to search"
                    searchable
                  />
                </InputWrapper>
              </Grid.Col>
            </Grid>
          )}
        </Stack>

        <Divider my="xl" />

        <Group justify="space-between">
          <Button
            variant="subtle"
            color="gray"
            onClick={() => navigate("/alerts")}
          >
            Cancel
          </Button>

          {activeTab < 3 && (
            <Button
              type="button"
              rightSection={<IconChevronRight size={16} />}
              style={{ borderRadius: "4px" }}
              onClick={() => handleTabChange((activeTab + 1).toString())}
            >
              Continue
            </Button>
          )}
          {activeTab == 3 && (
            <Button
              type="submit"
              rightSection={<IconDeviceFloppy size={16} />}
              style={{ borderRadius: "4px" }}
              disabled={!form.values.name?.trim()}
            >
              {isEditMode ? "Save" : "Create"} alert
            </Button>
          )}
        </Group>
      </Paper>
    </Grid.Col>
  )
}
